import { Badge } from '@talentmesh/ui/components/badge';
import { SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu } from '@talentmesh/ui/components/sidebar';
import { Skeleton } from '@talentmesh/ui/components/skeleton';
import { Star, Target } from 'lucide-react';
import { generatePath } from 'react-router';
import { ProfileDetailsSidebarProps } from './CandidatesOverviewNavigationSidebar';
import LineMenuItem from './LineMenuItem';
import { useRecruitmentId } from '@/Hooks';
import useCompanyId from '@/Hooks/UseCompanyId';
import useAISearchCounters from '@/Pages/Candidates/AiSearch/api/useAISearchCounters';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';

type CandidatesListProps = Pick<
    ProfileDetailsSidebarProps,
    'isIdentifiedAiSearchSelected' | 'isShortlistedAiSearchSelected'
>;

const CandidatesListNavGroup = ({
    isIdentifiedAiSearchSelected,
    isShortlistedAiSearchSelected,
}: CandidatesListProps) => {
    const assessmentId = useRecruitmentId();
    const companyId = useCompanyId();
    const { data: aiSearchCountersData, isLoading: isAISearchCountersLoading } = useAISearchCounters();
    const identifiedCount = aiSearchCountersData?.totalIdentified ?? 0;
    const shortlistedCount = aiSearchCountersData?.totalShortlisted ?? 0;

    return (
        <SidebarGroup className="p-0" data-cy="candidatesList">
            <SidebarGroupLabel className="text-muted-foreground text-xs tracking-wide uppercase">
                <span className="mx-2">{UIStrings.Candidates}</span>
            </SidebarGroupLabel>
            <SidebarGroupContent>
                <SidebarMenu>
                    <LineMenuItem
                        isActive={isIdentifiedAiSearchSelected}
                        to={generatePath(RoutePath.IdentifiedAiSearch, { assessmentId, companyId })}
                    >
                        <Target />
                        {UIStrings.Identified}
                        {isAISearchCountersLoading ? (
                            <Skeleton className="ml-auto h-full w-[3ch] rounded-4xl" />
                        ) : (
                            <Badge data-testid="identifiedCount" variant="secondary" className="ml-auto text-inherit">
                                {identifiedCount}
                            </Badge>
                        )}
                    </LineMenuItem>
                    <LineMenuItem
                        isActive={isShortlistedAiSearchSelected}
                        to={generatePath(RoutePath.ShortlistedAiSearch, { assessmentId, companyId })}
                    >
                        <Star />
                        {UIStrings.Shortlisted}
                        {isAISearchCountersLoading ? (
                            <Skeleton className="ml-auto h-full w-[3ch] rounded-4xl" />
                        ) : (
                            <Badge data-testid="shortlistedCount" variant="secondary" className="ml-auto text-inherit">
                                {shortlistedCount}
                            </Badge>
                        )}
                    </LineMenuItem>
                </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
    );
};

export default CandidatesListNavGroup;
